import React, { Suspense, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  createHtmlPortalNode,
  HtmlPortalNode,
  InPortal,
  OutPortal,
} from 'react-reverse-portal';

import ErrorBoundaryWithCrashReporting from '../../components/Error/ErrorBoundaryWithCrashReporting';
import LoadingFullscreen from '../../components/LoadingFullscreen';
import { getSessionCompanyId, getSessionToken } from '../../selectors/session';
import useCacheFeatureFlags from './hooks/useCacheFeatureFlags';
import useGetFeatureFlags from './hooks/useGetFeatureFlags';
import { FeatureFlag } from './types';

function RenderGetFeatureFlag({ portalNode }: { portalNode: HtmlPortalNode }) {
  useGetFeatureFlags();

  return <OutPortal node={portalNode} />;
}

function RenderCacheFeatureFlag({
  portalNode,
  features,
}: {
  portalNode: HtmlPortalNode;
  features: FeatureFlag[];
}) {
  const { useCache } = useCacheFeatureFlags();
  useCache(features);

  return <OutPortal node={portalNode} />;
}

export default function FeatureFlagProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const portalNode = useMemo(() => createHtmlPortalNode(), []);

  const companyId = useSelector(getSessionCompanyId);
  const isLoggedIn = Boolean(useSelector(getSessionToken));
  const { getCachedFeatureFlags } = useCacheFeatureFlags();
  const cachedFeatures = getCachedFeatureFlags();

  return (
    <>
      <InPortal node={portalNode}>{children}</InPortal>
      <ErrorBoundaryWithCrashReporting
        key={JSON.stringify([isLoggedIn, companyId])}
        fallback={<OutPortal node={portalNode} />}
        transactionName="feature-flag-provider"
      >
        <Suspense fallback={<LoadingFullscreen />}>
          <Choose>
            <When condition={cachedFeatures}>
              <RenderCacheFeatureFlag
                portalNode={portalNode}
                features={cachedFeatures ?? []}
              />
            </When>
            <Otherwise>
              <RenderGetFeatureFlag portalNode={portalNode} />
            </Otherwise>
          </Choose>
        </Suspense>
      </ErrorBoundaryWithCrashReporting>
    </>
  );
}
